import React from "react";
import { Card } from "../../../ui/primitives/Card";
import { Progress } from "../../../ui/primitives/Progress";
import { Stack } from "../../../ui/primitives/Stack";
import { Text } from "../../../ui/primitives/Text";
import type { TextColor } from "../../../ui/primitives/Text";

export interface StaffDailySummaryProps {
  /** Shift-checklist items ticked off today, across every shift. */
  doneCount: number;
  /** Shift-checklist items on today's shifts, across every shift. */
  totalCount: number;
  /** Jobs allocated to this person today (no completion state of their own). */
  allocatedCount: number;
  /** Calls still open — the queue shrinks as outcomes are recorded. */
  callsCount: number;
  /** BD follow-ups due today or overdue. Its own figure, not folded into the allocated
   * count: BD renders in its own card, so adding it there would make the stat disagree
   * with the card it summarises. */
  bdCount?: number;
}

interface StatProps {
  label: string;
  value: number | string;
  color?: TextColor;
  "data-testid"?: string;
}

// `as` overrides the heading element the type scale would otherwise pick: a bare number is
// not a section heading, and letting it render an <h3> puts context-free digits into
// screen-reader heading navigation.
const Stat: React.FC<StatProps> = ({ label, value, color, "data-testid": dataTestId }) => (
  <Stack gap={0}>
    <Text variant="h3" as="span" color={color} data-testid={dataTestId}>
      {value}
    </Text>
    <Text variant="caption">{label}</Text>
  </Stack>
);

/**
 * The day at a glance, above the work itself: how much of the shift checklist is done,
 * and what else is still outstanding.
 *
 * Only the checklist feeds the progress bar. Allocations carry no completion state and
 * the calls endpoint returns open calls only — counting either would shrink the
 * denominator as work got done and leave the bar stuck at zero, so they are shown as
 * plain outstanding counts instead.
 *
 * The two scopes are labelled apart on purpose: the checklist is the shift's and is
 * ticked off by whoever is on it, while the jobs and calls belong to the person signed in.
 */
const StaffDailySummary: React.FC<StaffDailySummaryProps> = ({
  doneCount,
  totalCount,
  allocatedCount,
  callsCount,
  bdCount = 0,
}) => {
  const percent = totalCount === 0 ? 0 : Math.round((doneCount / totalCount) * 100);
  const remaining = Math.max(totalCount - doneCount, 0);
  const complete = totalCount > 0 && remaining === 0;

  return (
    <Card data-testid="staff-daily-summary" style={{ marginBottom: 16 }}>
      <Stack gap={3}>
        {totalCount > 0 ? (
          <>
            <Stack direction="horizontal" gap={3} justify="space-between" align="baseline" wrap>
              <Text variant="h5" as="div">
                Today&apos;s progress
              </Text>
              {/* Spelled out because this one number is club-wide while the two beside it
                  are personal: the checklist belongs to the shift, and whoever is on it
                  ticks items off for everyone. */}
              <Text color="secondary" data-testid="staff-daily-summary-count">
                {doneCount} of {totalCount} shift tasks done — all staff
              </Text>
            </Stack>
            <Progress
              percent={percent}
              status={complete ? "success" : "active"}
              data-testid="staff-daily-summary-progress"
            />
          </>
        ) : null}
        <Stack direction="horizontal" gap={6} wrap>
          {totalCount > 0 ? (
            <Stat
              label={complete ? "All done" : "Shift tasks left"}
              value={remaining}
              color={complete ? "success" : undefined}
              data-testid="staff-daily-summary-remaining"
            />
          ) : null}
          <Stat
            label={allocatedCount === 1 ? "Job for you" : "Jobs for you"}
            value={allocatedCount}
            data-testid="staff-daily-summary-allocated"
          />
          {bdCount > 0 ? (
            <Stat
              label={bdCount === 1 ? "BD follow-up" : "BD follow-ups"}
              value={bdCount}
              data-testid="staff-daily-summary-bd"
            />
          ) : null}
          <Stat
            label={callsCount === 1 ? "Call for you" : "Calls for you"}
            value={callsCount}
            data-testid="staff-daily-summary-calls"
          />
        </Stack>
      </Stack>
    </Card>
  );
};

export default StaffDailySummary;
